/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { MutableRef } from 'preact/hooks';
import { SizingInfo, TableColumn } from '../Table';
import {
  getCellType,
  getFirstFocusableCell,
  getFirstFocusableRowKey,
  getLastFocusableRowKey,
  logicalCellExtractor,
  getRowKey,
  hasFooterCells,
  hasNoDataCell,
  getScrollerBottomOffset,
  getRowKeyForRowIndex,
  getRowIndexForRowKey
} from './TableDomUtils';
import {
  _getVerticalOverflowDiff,
  applyScrollDetail,
  getVerticalScrollPosition
} from './TableScrollUtils';
import { Cell, CurrentCellDetail } from '../../UNSAFE_TableView';
import { findElementByKey } from '../../utils/PRIVATE_collectionUtils';

/**
 * Helper function to compare two given cell descriptors.
 */
export function cellComparator<K, C>(cell1?: Cell<K, C> | null, cell2?: Cell<K, C> | null) {
  if (cell1 == null) {
    return cell2 == null;
  }
  if (cell2 == null) {
    return false;
  }
  return (
    cell1?.rowKey === cell2?.rowKey &&
    cell1?.columnKey === cell2?.columnKey &&
    cell1?.type === cell2?.type
  );
}

/**
 * Helper function to handle focus somewhere in the Table.
 */
export function handleElementFocus<K extends string | number, D, C>(
  root: HTMLDivElement,
  tableId: string,
  targetElement: HTMLElement,
  columnsArray: TableColumn<K, D, C>[],
  sizingInfoRef: MutableRef<SizingInfo>,
  pendingCurrentCellRef: MutableRef<Cell<K, C> | undefined>,
  blurFromWindowRef: MutableRef<boolean>,
  pendingSelectionExtensionRef: MutableRef<boolean>,
  isRtl: boolean,
  currentCell?: Cell<K, C>,
  onCurrentCellChange?: (detail: CurrentCellDetail<K, C>) => void,
  fullColumnWidths?: number[]
) {
  // don't scroll current into view if previous blur was due to window focus loss
  const scrollCurrentIntoView = !blurFromWindowRef.current;
  blurFromWindowRef.current = false;

  if (root === targetElement) {
    // handle overall root element receiving focus
    if (pendingCurrentCellRef.current == null && !pendingSelectionExtensionRef.current) {
      if (currentCell == null) {
        if (onCurrentCellChange != null) {
          const firstCell = getFirstFocusableCell(root, tableId) as Cell<K, C>;
          if (firstCell != null) {
            onCurrentCellChange({ value: firstCell as Cell<K, C> });
            if (scrollCurrentIntoView) {
              // scroll currentCell into view if focus is not due to pointer down
              applyScrollDetail(
                root,
                tableId,
                columnsArray,
                sizingInfoRef.current,
                { cell: firstCell, locationX: 'inView', locationY: 'inView' },
                isRtl,
                fullColumnWidths
              );
            }
          }
        }
      } else if (scrollCurrentIntoView) {
        // scroll currentCell into view if focus is not due to pointer down
        applyScrollDetail(
          root,
          tableId,
          columnsArray,
          sizingInfoRef.current,
          { cell: currentCell, locationX: 'inView', locationY: 'inView' },
          isRtl,
          fullColumnWidths
        );
      }
    }
    pendingCurrentCellRef.current = undefined;
  } else if (getCellType(targetElement) != null) {
    // handle individual focusable regions receiving focus
    if (!pendingSelectionExtensionRef.current && onCurrentCellChange != null) {
      const cell = logicalCellExtractor(
        targetElement,
        tableId,
        `[data-oj-table-focusable='${tableId}']`
      ) as Cell<K, C>;
      if (!cellComparator(currentCell, cell)) {
        pendingCurrentCellRef.current = cell;
        onCurrentCellChange({ value: cell });
      }
      // scroll currentCell into view if focus is not due to pointer down
      if (currentCell != null && pendingCurrentCellRef.current == null && scrollCurrentIntoView) {
        applyScrollDetail(
          root,
          tableId,
          columnsArray,
          sizingInfoRef.current,
          { cell: currentCell, locationX: 'inView', locationY: 'inView' },
          isRtl,
          fullColumnWidths
        );
      }
    }
    // move focus to the overall root element
    root.focus({ preventScroll: true });
  }
}

/**
 * Helper function to handle an 'Up' or 'Down' arrow key press.
 * Up - Moves to the same cell in the previous row (including headers and footers). No op if already on the first row.
 * Down - Moves to the same cell in the next row (including headers and footers). No op if already on the last row.
 */
export function handleAdjacentRowGesture<K extends string | number, D, C>(
  root: HTMLDivElement,
  tableId: string,
  columnsArray: TableColumn<K, D, C>[],
  sizingInfoRef: MutableRef<SizingInfo>,
  initialCell: Cell<K, C>,
  onCurrentCellChange: (detail: CurrentCellDetail<K, C>) => void,
  isPrevious: boolean,
  isRtl: boolean,
  fullColumnWidths?: number[]
) {
  let newCurrentCell;
  const cellType = initialCell.type;
  if (cellType !== 'noData') {
    const columnKey = initialCell.columnKey;
    if (cellType === 'data') {
      const currentRowKey = initialCell.rowKey;
      const currentRowElement = findElementByKey(
        root,
        currentRowKey as string | number,
        `[data-oj-table-data-row='${tableId}']`
      );
      if (currentRowElement != null) {
        const adjacentRowElement = isPrevious
          ? (currentRowElement.previousElementSibling as HTMLElement)
          : (currentRowElement.nextElementSibling as HTMLElement);
        if (adjacentRowElement != null) {
          const rowKey = getRowKey(adjacentRowElement) as K;
          if (rowKey != null) {
            newCurrentCell = { rowKey: rowKey, columnKey: columnKey, type: 'data' } as Cell<K, C>;
            onCurrentCellChange({ value: newCurrentCell });
          }
        } else if (isPrevious) {
          newCurrentCell = { columnKey: initialCell.columnKey, type: 'header' } as Cell<K, C>;
          onCurrentCellChange({ value: newCurrentCell });
        } else if (hasFooterCells(root, tableId)) {
          newCurrentCell = { columnKey: initialCell.columnKey, type: 'footer' } as Cell<K, C>;
          onCurrentCellChange({ value: newCurrentCell });
        }
      }
    } else if (cellType === 'header' && !isPrevious) {
      const rowKey = getFirstFocusableRowKey(root, tableId) as K;
      if (rowKey != null) {
        newCurrentCell = { rowKey: rowKey, columnKey: columnKey, type: 'data' } as Cell<K, C>;
        onCurrentCellChange({ value: newCurrentCell });
      } else if (hasNoDataCell(root, tableId)) {
        newCurrentCell = { type: 'noData' } as Cell<K, C>;
        onCurrentCellChange({ value: newCurrentCell });
      } else if (hasFooterCells(root, tableId)) {
        newCurrentCell = { columnKey: initialCell.columnKey, type: 'footer' } as Cell<K, C>;
        onCurrentCellChange({ value: newCurrentCell });
      }
    } else if (cellType === 'footer' && isPrevious) {
      const rowKey = getLastFocusableRowKey(root, tableId) as K;
      if (rowKey != null) {
        newCurrentCell = { rowKey: rowKey, columnKey: columnKey, type: 'data' } as Cell<K, C>;
        onCurrentCellChange({ value: newCurrentCell });
      } else if (hasNoDataCell(root, tableId)) {
        newCurrentCell = { type: 'noData' } as Cell<K, C>;
        onCurrentCellChange({ value: newCurrentCell });
      } else {
        newCurrentCell = { columnKey: initialCell.columnKey, type: 'header' } as Cell<K, C>;
        onCurrentCellChange({ value: newCurrentCell });
      }
    }
  } else {
    const newColumnKey = _getBoundaryColumnKey(columnsArray, true);
    if (isPrevious) {
      newCurrentCell = { columnKey: newColumnKey, type: 'header' } as Cell<K, C>;
      onCurrentCellChange({ value: newCurrentCell });
    } else if (hasFooterCells(root, tableId)) {
      newCurrentCell = { columnKey: newColumnKey, type: 'footer' } as Cell<K, C>;
      onCurrentCellChange({ value: newCurrentCell });
    }
  }
  // scroll 'current' element into view
  if (newCurrentCell != null) {
    applyScrollDetail(
      root,
      tableId,
      columnsArray,
      sizingInfoRef.current,
      { cell: newCurrentCell, locationX: 'inView', locationY: 'inView' },
      isRtl,
      fullColumnWidths
    );
  } else {
    applyScrollDetail(
      root,
      tableId,
      columnsArray,
      sizingInfoRef.current,
      { cell: initialCell, locationX: 'inView', locationY: 'inView' },
      isRtl,
      fullColumnWidths
    );
  }
}

/**
 * Helper function to handle a 'PageUp' or 'PageDown' arrow key press.
 * PageUp - Moves to the same cell in the first visible row in the current viewport,
 * and scrolls the Table so it becomes the last visible row in the viewport when able.
 * PageDown - Moves to the same cell in the last visible row in the current viewport,
 * and scrolls the Table so it becomes the first visible row in the viewport when able.
 */
export function handlePageRowGesture<K extends string | number, D, C>(
  root: HTMLDivElement,
  tableId: string,
  columnsArray: TableColumn<K, D, C>[],
  sizingInfoRef: MutableRef<SizingInfo>,
  initialCell: Cell<K, C>,
  onCurrentCellChange: (detail: CurrentCellDetail<K, C>) => void,
  isPrevious: boolean,
  isRtl: boolean,
  fullColumnWidths?: number[]
) {
  const rowElements = root.querySelectorAll(`[data-oj-table-data-row='${tableId}']`);
  if (rowElements.length > 0) {
    const verticalScrollPosition = getVerticalScrollPosition(root, tableId);
    const verticalRowIndex = getRowIndexForRowKey(root, tableId, verticalScrollPosition.rowKey!);
    let targetRowIndex;
    if (isPrevious) {
      if (verticalScrollPosition.offsetY === 0) {
        targetRowIndex = Math.max(verticalRowIndex! - 1, 0);
      } else {
        targetRowIndex = verticalRowIndex!;
      }
    } else {
      const scrollerRect = root.getBoundingClientRect();
      const scrollbarHeight =
        sizingInfoRef.current.boxHeight! - sizingInfoRef.current.contentHeight!;
      const scrollerBottom =
        scrollerRect.bottom + getScrollerBottomOffset(root, tableId) + scrollbarHeight;
      targetRowIndex = verticalRowIndex!;
      while (rowElements.length > targetRowIndex + 1) {
        targetRowIndex += 1;
        const elementBottom = rowElements[targetRowIndex].getBoundingClientRect().bottom;
        if (elementBottom > scrollerBottom) {
          break;
        }
      }
    }
    const newRowKey = getRowKeyForRowIndex(root, tableId, targetRowIndex) as K;
    if (newRowKey != null) {
      const newCurrentCell = {
        rowKey: newRowKey,
        columnKey: initialCell.columnKey,
        type: 'data'
      } as Cell<K, C>;
      if (newRowKey !== initialCell.rowKey) {
        onCurrentCellChange({ value: newCurrentCell });
      }
      applyScrollDetail(
        root,
        tableId,
        columnsArray,
        sizingInfoRef.current,
        { cell: newCurrentCell, locationX: 'inView', locationY: isPrevious ? 'bottom' : 'top' },
        isRtl,
        fullColumnWidths
      );
    }
  }
}

/**
 * Helper function to get the column key next to the given column key in the specified direction. Returns
 * null if there is no adjacent column in the direction specified.
 */
function _getAdjacentColumnKey<K, D, C>(
  initialKey: C,
  columnsArray: TableColumn<K, D, C>[],
  isPrevious: boolean
) {
  let currentIndex = -1;
  const columnsCount = columnsArray.length;
  for (let i = 0; i < columnsCount; i++) {
    if (columnsArray[i].key === initialKey) {
      currentIndex = i;
      break;
    }
  }
  if (isPrevious && currentIndex !== 0) {
    return columnsArray[currentIndex - 1].key;
  } else if (!isPrevious && currentIndex !== columnsCount - 1) {
    return columnsArray[currentIndex + 1].key;
  }
  return null;
}

/**
 * Helper function to handle a 'Previous' or 'Next' arrow key press.
 * Previous - Moves to the previous cell in the current row. No op if already on the first cell in that row.
 * Next - Moves to the next cell in the current row. No op if already on the last cell in that row.
 */
export function handleAdjacentColumnGesture<K extends string | number, D, C>(
  root: HTMLDivElement,
  tableId: string,
  columnsArray: TableColumn<K, D, C>[],
  sizingInfoRef: MutableRef<SizingInfo>,
  initialCell: Cell<K, C>,
  onCurrentCellChange: (detail: CurrentCellDetail<K, C>) => void,
  isPrevious: boolean,
  isRtl: boolean,
  fullColumnWidths?: number[]
) {
  let newCurrentCell;
  const cellType = initialCell.type;
  if (cellType !== 'noData') {
    const initialKey = initialCell.columnKey;
    const newKey = _getAdjacentColumnKey(initialKey, columnsArray, isPrevious);
    if (newKey != null) {
      if (cellType === 'data') {
        newCurrentCell = { rowKey: initialCell.rowKey, columnKey: newKey, type: cellType };
        onCurrentCellChange({ value: newCurrentCell });
      } else {
        newCurrentCell = { columnKey: newKey, type: cellType };
        onCurrentCellChange({ value: newCurrentCell });
      }
    }
  }
  // scroll 'current' element into view
  if (newCurrentCell != null) {
    applyScrollDetail(
      root,
      tableId,
      columnsArray,
      sizingInfoRef.current,
      { cell: newCurrentCell, locationX: 'inView', locationY: 'inView' },
      isRtl,
      fullColumnWidths
    );
  } else {
    applyScrollDetail(
      root,
      tableId,
      columnsArray,
      sizingInfoRef.current,
      { cell: initialCell, locationX: 'inView', locationY: 'inView' },
      isRtl,
      fullColumnWidths
    );
  }
}

/**
 * Helper function to get the first or last column key.
 */
function _getBoundaryColumnKey<K, D, C>(columnsArray: TableColumn<K, D, C>[], isFirst: boolean) {
  if (isFirst) {
    return columnsArray[0].key;
  }
  return columnsArray[columnsArray.length - 1].key;
}

/**
 * Helper function to handle a 'Home' or 'End' key press.
 * Home - Jumps to the first cell in the current row. No op if already on the first cell in that row.
 * Ctrl/Cmd + Home - Jumps to the first cell in the first data row. If no data rows are present,
 * jumps to the first cell in the current region (header or footer). No op if already on that cell.
 * End - Jumps to the last cell in the current row. No op if already on the last cell in that row.
 * Ctrl/Cmd + End - Jumps to the last cell in the last data row. If no data rows are present,
 * jumps to the last cell in the current region (header or footer). No op if already on that cell.
 */
export function handleJumpColumnGesture<K extends string | number, D, C>(
  root: HTMLDivElement,
  tableId: string,
  columnsArray: TableColumn<K, D, C>[],
  sizingInfoRef: MutableRef<SizingInfo>,
  initialCell: Cell<K, C>,
  onCurrentCellChange: (detail: CurrentCellDetail<K, C>) => void,
  isPrevious: boolean,
  includeRows: boolean,
  isRtl: boolean,
  fullColumnWidths?: number[]
) {
  let newCurrentCell;
  const cellType = initialCell.type;
  if (cellType !== 'noData') {
    const initialKey = initialCell.columnKey;
    const newKey = _getBoundaryColumnKey(columnsArray, isPrevious);
    if (includeRows) {
      if (hasNoDataCell(root, tableId)) {
        newCurrentCell = {
          type: 'noData'
        } as Cell<K, C>;
        if (!cellComparator(newCurrentCell, initialCell)) {
          onCurrentCellChange({ value: newCurrentCell });
        }
      } else {
        const newRowKey = isPrevious
          ? (getFirstFocusableRowKey(root, tableId) as K)
          : (getLastFocusableRowKey(root, tableId) as K);
        if (newRowKey != null) {
          newCurrentCell = {
            rowKey: newRowKey,
            columnKey: newKey,
            type: 'data'
          } as Cell<K, C>;
          if (!cellComparator(newCurrentCell, initialCell)) {
            onCurrentCellChange({ value: newCurrentCell });
          }
        }
      }
    } else if (newKey !== initialKey) {
      if (cellType === 'data') {
        newCurrentCell = { rowKey: initialCell.rowKey, columnKey: newKey, type: cellType };
        onCurrentCellChange({ value: newCurrentCell });
      } else {
        newCurrentCell = { columnKey: newKey, type: cellType };
        onCurrentCellChange({ value: newCurrentCell });
      }
    }
  }
  // scroll 'current' element into view
  if (newCurrentCell != null) {
    applyScrollDetail(
      root,
      tableId,
      columnsArray,
      sizingInfoRef.current,
      { cell: newCurrentCell, locationX: 'inView', locationY: 'inView' },
      isRtl,
      fullColumnWidths
    );
  } else {
    applyScrollDetail(
      root,
      tableId,
      columnsArray,
      sizingInfoRef.current,
      { cell: initialCell, locationX: 'inView', locationY: 'inView' },
      isRtl,
      fullColumnWidths
    );
  }
}
